import notification from "antd/es/notification";
import { push } from "connected-react-router";
import { isEqual, omit, set } from "lodash-es";
import { defineMessages } from "react-intl";
import { all, call, delay, put, race, select, takeEvery, takeLatest } from "redux-saga/effects";
import invariant from "tiny-invariant";
import * as uuid from "uuid";

import ActivityStatusOption from "@mapmycustomers/shared/enum/activity/ActivityStatusOption";
import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import Identified from "@mapmycustomers/shared/types/base/Identified";
import { Activity, Company, Deal, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import ActivityType from "@mapmycustomers/shared/types/entity/activities/ActivityType";
import Note from "@mapmycustomers/shared/types/entity/common/Note";
import IField from "@mapmycustomers/shared/types/fieldModel/IField";
import { RawFile } from "@mapmycustomers/shared/types/File";
import Organization from "@mapmycustomers/shared/types/Organization";
import User from "@mapmycustomers/shared/types/User";
import { FilterModel } from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";
import SortModel from "@mapmycustomers/shared/types/viewModel/internalModel/SortModel";
import ListResponse from "@mapmycustomers/shared/types/viewModel/ListResponse";
import { PinLegendsFilter } from "@mapmycustomers/shared/types/viewModel/platformModel/PlatformFilterModel";

import getSuccessNotificationNode from "@app/component/createEditEntity/util/getSuccessNotificationNode";
import getFileRemovedNotificationNode from "@app/component/preview/util/getFileRemovedNotificationNode";
import getStartRange from "@app/component/preview/util/getStartRange";
import getYourDownloadWillStartShortlyNode from "@app/component/preview/util/getYourDownloadWillStartShortlyNode";
import i18nService from "@app/config/I18nService";
import localSettings from "@app/config/LocalSettings";
import Path from "@app/enum/Path";
import { recapRangeIntervalMap } from "@app/enum/preview/RecapRange";
import SettingPath from "@app/enum/settings/SettingPath";
import { getActiveActivityTypes } from "@app/store/activity";
import { FieldValidationErrors } from "@app/store/api/ApiService";
import { callApi } from "@app/store/api/callApi";
import getNextAssociationsState, {
  NextAssociationsState,
} from "@app/store/associations/getNextAssociationsState";
import { handleError } from "@app/store/errors/actions";
import {
  getCurrentUser,
  getOrganization,
  getOrganizationId,
  shouldIncludeSharedActivities,
} from "@app/store/iam";
import { updateMetadata } from "@app/store/iam/actions";
import { notifyAboutChanges } from "@app/store/uiSync/actions";
import FileListItem from "@app/types/FileListItem";
import getParentActivitiesFilter from "@app/util/activity/getParentActivitiesFilter";
import { NOTIFICATION_DURATION_WITH_ACTION } from "@app/util/consts";
import { allSettled, SettleResult } from "@app/util/effects";
import dealFieldModel, { DealFieldName } from "@app/util/fieldModel/DealFieldModel";
import noteFieldModel, { NoteFieldName } from "@app/util/fieldModel/NoteFieldModel";
import { downloadFileByUrl } from "@app/util/file";
import { createEmptyFilterModel } from "@app/util/filters/utils";
import { activityLayoutModel } from "@app/util/layout/impl";
import loggingService from "@app/util/logging";
import getPinLegendsFilter from "@app/util/map/getPinLegendsFilter";
import { convertToPlatformSortModel } from "@app/util/viewModel/convertSort";
import { convertToPlatformFilterModel } from "@app/util/viewModel/convertToPlatformFilterModel";

import {
  addDealParentCompany,
  addDealParentPerson,
  bulkUpdateFiles,
  changeActivitiesRecapRange,
  changeActivitiesSelectedActivityTypes,
  changeAssociatedEntities,
  createDealNote,
  deleteDeal,
  deleteDealFile,
  deleteDealNote,
  downloadDealFile,
  fetchDeal,
  fetchDealActivities,
  fetchDealActivitiesCompletedByType,
  fetchDealActivitiesTotal,
  fetchNotes,
  fetchPreviewData,
  fetchRelatedEntities,
  fetchThumbnail,
  goToCalendarSettings,
  postponeActivity,
  relateUnrelateEntities,
  removeDealParentCompany,
  removeDealParentPerson,
  toggleCompleteActivity,
  updateActivityNote,
  updateDeal,
  updateDealFrequency,
  updateDealNote,
  uploadDealFiles,
} from "./actions";
import DealRecordData from "./DealRecordData";
import { getDeal, getNotesFilter, getNotesSort, getRecordData } from "./selectors";

const processAssociatedEntity = <T extends Company | Person>(
  originalEntity: T | undefined,
  parentEntityResult: SettleResult<T | undefined>
): T | undefined => {
  if (!parentEntityResult.error) {
    return parentEntityResult.result;
  } else if (originalEntity) {
    return {
      ...originalEntity,
      accessStatus: {
        ...(originalEntity.accessStatus ?? {}),
        delete: false,
        read: false,
        update: false,
      },
    };
  }
  return originalEntity;
};

const messages = defineMessages({
  addCompanySuccess: {
    id: "dealRecordPane.addCompany.success",
    defaultMessage: "Deal successfully related to Company",
    description: "Deal successfully related to Company message",
  },
  addPersonSuccess: {
    id: "dealRecordPane.addPerson.success",
    defaultMessage: "Deal successfully related to Person",
    description: "Deal successfully related to Person message",
  },
  deleteSuccess: {
    id: "dealRecordPane.delete.success.title",
    defaultMessage: "Deal successfully deleted",
    description: "Deal deleted success message",
  },
  deleteSuccessDescription: {
    id: "dealRecordPane.delete.success.description",
    defaultMessage: "To restore this record, go to Data in Settings.",
    description: "Deal deleted success description",
  },
  postponeSuccess: {
    id: "dealRecordPane.activities.postpone.success",
    defaultMessage: "Activity postponed successfully",
    description: "Activity postpone success message",
  },
  toggleCompleteSuccess: {
    id: "dealRecordPane.activities.toggleComplete.success",
    defaultMessage: 'Activity{completed, select, true {} other { is no longer}} marked as "Done"',
    description: "Activity toggle complete success message",
  },
  updateError: {
    id: "dealRecordPane.update.error",
    defaultMessage: "Error updating deal. Please try again or contact support",
    description: "Deal update error message",
  },
  updateSuccess: {
    id: "dealRecordPane.update.success",
    defaultMessage: "Deal updated successfully",
    description: "Deal updated success message",
  },
});

export function* onFetchPreviewData({
  payload: dealId,
}: ReturnType<typeof fetchPreviewData.request>) {
  try {
    const organization: Organization = yield select(getOrganization);

    const pinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.DEAL]);
    const deal: Deal | undefined = yield callApi("fetchDeal", organization.id, dealId, {
      includeAccessStatus: true,
      includeCustomFields: true,
      includeFiles: true,
      includeGroups: true,
      includeTerritories: true,
      includeUsersWithAccess: true,
      pinLegends,
    });

    if (!deal) {
      yield put(fetchPreviewData.failure());
      return;
    }

    const notesField: IField | undefined = yield call(
      [dealFieldModel, dealFieldModel.getByName],
      DealFieldName.NOTES
    );

    const includeSharedActivities: boolean = yield select(shouldIncludeSharedActivities);

    const notesSort: SortModel = yield select(getNotesSort);

    const [notes, activitiesResponse, uncompletedActivitiesResponse]: [
      ListResponse<Note>,
      ListResponse<Activity>,
      ListResponse<Activity>,
    ] = yield all([
      notesField?.isReadable
        ? callApi("fetchNotes", EntityType.DEAL, deal.id, {
            $limit: 1000,
            $order: convertToPlatformSortModel(notesSort),
          })
        : { accessible: 0, data: [] },
      callApi("fetchActivities", organization.id, {
        $columns: ["id"],
        $filters: {
          $and: [
            {
              dealId,
            },
          ],
          includeDealSharedActivities: includeSharedActivities,
        },
        $limit: 0,
      }),
      callApi("fetchActivities", organization.id, {
        $columns: ["id", "startAt"],
        $filters: {
          $and: [
            {
              completed: false,
              dealId,
            },
          ],
          includeAccessStatus: true,
          includeDealSharedActivities: includeSharedActivities,
        },
        $limit: 1000,
        $order: "startAt",
      }),
    ]);

    const companyPinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [
      EntityType.COMPANY,
    ]);
    const personPinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.PERSON]);

    const [companyParent, personParent]: [
      SettleResult<Company | undefined>,
      SettleResult<Person | undefined>,
    ] = yield allSettled([
      deal.account
        ? callApi("fetchCompany", organization.id, deal.account.id, {
            includeAccessStatus: true,
            includeCustomFields: true,
            includeGroups: true,
            includeTerritories: true,
            pinLegends: companyPinLegends,
          })
        : undefined,
      deal.contact
        ? callApi("fetchPerson", organization.id, deal.contact.id, {
            includeAccessStatus: true,
            includeCustomFields: true,
            includeGroups: true,
            includeTerritories: true,
            pinLegends: personPinLegends,
          })
        : undefined,
    ]);

    const user: User = yield select(getCurrentUser);

    // get stored selected activity types ids
    const activityTypes: ActivityType[] = yield select(getActiveActivityTypes);
    const selectedActivityTypesIds = user?.metaData?.recapChartTypesIds;

    // find corresponding activity types
    const selectedActivityTypes: ActivityType[] = selectedActivityTypesIds
      ? activityTypes.filter(({ id }) => selectedActivityTypesIds.includes(id))
      : activityTypes;

    const associations: NextAssociationsState = yield call(getNextAssociationsState, {
      company: !companyParent.error ? companyParent.result : deal.account,
      entityType: EntityType.DEAL,
      person: !personParent.error ? personParent.result : deal.contact,
    });

    const associatedCompany = processAssociatedEntity(deal.account, companyParent);
    const associatedPerson = processAssociatedEntity(deal.contact, personParent);

    yield put(
      fetchPreviewData.success({
        recordData: {
          activities: [],
          activitiesCompletedByType: [],
          activitiesTotal: activitiesResponse.total,
          associations: { ...associations, associatedCompany, associatedPerson },
          deal,
          files: deal.files ?? [],
          notes: notes.data,
          notesTotal: notes.accessible,
          uncompletedActivities: uncompletedActivitiesResponse.data,
        },
        selectedActivityTypes,
      })
    );
  } catch (error) {
    yield put(fetchPreviewData.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchDealActivities({
  payload: {
    activityTypes: selectedActivityTypes,
    dealId,
    filter,
    order,
    recapRange,
    search,
    selectedAssignees,
  },
}: ReturnType<typeof fetchDealActivities.request>) {
  try {
    const recapInterval = recapRangeIntervalMap[recapRange];
    const organization: Organization = yield select(getOrganization);
    const includeSharedActivities: boolean = yield select(shouldIncludeSharedActivities);
    const activityTypes: ActivityType[] = yield select(getActiveActivityTypes);

    const activitiesResponse: ListResponse<Activity> = yield callApi(
      "fetchActivities",
      organization.id,
      {
        $filters: {
          $and: [
            {
              completed:
                filter === ActivityStatusOption.COMPLETED
                  ? true
                  : filter === ActivityStatusOption.OVERDUE
                    ? false
                    : undefined,
              ...(activityTypes.length === selectedActivityTypes.length
                ? {}
                : { "crmActivityType.id": { $in: selectedActivityTypes.map(({ id }) => id) } }),
              dealId,
              name: search ? { $in: search.trim() } : undefined,
              startAt: getStartRange(recapInterval, filter),
              ...(selectedAssignees.length ? { assigneeId: { $in: selectedAssignees } } : {}),
            },
            getParentActivitiesFilter(),
          ],
          includeAccessStatus: true,
          includeCustomFields: true,
          includeDealSharedActivities: includeSharedActivities,
          includeFiles: true,
        },
        $limit: 1000,
        $order: convertToPlatformSortModel(order),
      }
    );
    yield put(fetchDealActivities.success(activitiesResponse.data));
  } catch (error) {
    yield put(fetchDealActivities.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchDealActivitiesCompletedByType({
  payload: {
    activityTypes: selectedActivityTypes,
    dealId,
    filter,
    order,
    recapRange,
    selectedAssignees,
  },
}: ReturnType<typeof fetchDealActivitiesCompletedByType.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const recapInterval = recapRangeIntervalMap[recapRange];
    const includeSharedActivities: boolean = yield select(shouldIncludeSharedActivities);
    const activityTypes: ActivityType[] = yield select(getActiveActivityTypes);

    const activitiesResponse: ListResponse<Activity> = yield callApi(
      "fetchActivities",
      organization.id,
      {
        $filters: {
          $and: [
            {
              ...(activityTypes.length === selectedActivityTypes.length
                ? {}
                : { "crmActivityType.id": { $in: selectedActivityTypes.map(({ id }) => id) } }),
              dealId,
              startAt: getStartRange(recapInterval, filter),
              ...(selectedAssignees.length ? { assigneeId: { $in: selectedAssignees } } : {}),
            },
            getParentActivitiesFilter(),
          ],
          includeAccessStatus: true,
          includeCustomFields: true,
          includeDealSharedActivities: includeSharedActivities,
        },
        $limit: 1000,
        $order: convertToPlatformSortModel(order),
      }
    );
    yield put(fetchDealActivitiesCompletedByType.success(activitiesResponse.data));
  } catch (error) {
    yield put(fetchDealActivitiesCompletedByType.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchThumbnail({ payload }: ReturnType<typeof fetchThumbnail>) {
  try {
    const org: Organization = yield select(getOrganization);
    const deal: Deal = yield select(getDeal);
    const fileData: Blob = yield callApi(
      "fetchFile",
      org.id,
      payload.fileId,
      false,
      true,
      EntityType.DEAL,
      deal.id,
      { responseType: "blob" }
    );
    yield call(payload.callback, fileData);
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onFetchDealActivitiesTotal({
  payload,
}: ReturnType<typeof fetchDealActivitiesTotal.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const includeSharedActivities: boolean = yield select(shouldIncludeSharedActivities);

    const activitiesResponse: ListResponse<Activity> = yield callApi(
      "fetchActivities",
      organization.id,
      {
        $columns: ["id"],
        $filters: {
          $and: [
            {
              dealId: payload,
            },
          ],
          includeDealSharedActivities: includeSharedActivities,
        },
        $limit: 0,
      }
    );
    yield put(fetchDealActivitiesTotal.success(activitiesResponse.total));
  } catch (error) {
    yield put(fetchDealActivitiesTotal.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchDeal({ payload: deal }: ReturnType<typeof fetchDeal.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const response: Deal = yield callApi("fetchDeal", organization.id, deal.id, {
      includeAccessStatus: true,
      includeGroups: true,
      includeTerritories: true,
    });
    yield put(fetchDeal.success(response));
  } catch (error) {
    yield put(fetchDeal.failure());
    yield put(handleError({ error }));
  }
}

function* onAddDealParentCompany({
  payload: { id, parentCompanyId },
}: ReturnType<typeof addDealParentCompany.request>) {
  try {
    const org: Organization = yield select(getOrganization);

    const pinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.COMPANY]);

    const [deal, parentCompany]: [Deal, Company] = yield all([
      callApi("updateDeal", org.id, undefined, {
        id,
        account: { id: parentCompanyId },
      }),
      callApi("fetchCompany", org.id, parentCompanyId, {
        includeAccessStatus: true,
        includeCustomFields: true,
        includeGroups: true,
        pinLegends,
      }),
    ]);

    notification.success({
      message: i18nService.formatMessage(
        messages.addCompanySuccess,
        "Deal successfully related to Company"
      ),
    });

    yield put(addDealParentCompany.success(parentCompany));
    yield put(notifyAboutChanges({ entityType: EntityType.DEAL, updated: [deal] }));
  } catch (error) {
    yield put(addDealParentCompany.failure());
    yield put(handleError({ error }));
  }
}

function* onRemoveDealParentCompany({
  payload,
}: ReturnType<typeof removeDealParentCompany.request>) {
  try {
    const org: Organization = yield select(getOrganization);

    const deal: Deal = yield callApi("updateDeal", org.id, undefined, {
      id: payload,
      account: null,
    });

    yield put(removeDealParentCompany.success(payload));
    yield put(notifyAboutChanges({ entityType: EntityType.DEAL, updated: [deal] }));
  } catch (error) {
    yield put(removeDealParentCompany.failure(payload));
    yield put(handleError({ error }));
  }
}

function* onAddDealParentPerson({
  payload: { id, parentPersonId },
}: ReturnType<typeof addDealParentPerson.request>) {
  try {
    const org: Organization = yield select(getOrganization);

    const pinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.PERSON]);

    const [deal, parentPerson]: [Deal, Person] = yield all([
      callApi("updateDeal", org.id, undefined, {
        id,
        contact: { id: parentPersonId },
      }),
      callApi("fetchPerson", org.id, parentPersonId, {
        includeAccessStatus: true,
        includeCustomFields: true,
        includeGroups: true,
        pinLegends,
      }),
    ]);

    notification.success({
      message: i18nService.formatMessage(
        messages.addPersonSuccess,
        "Deal successfully related to Person"
      ),
    });

    yield put(addDealParentPerson.success(parentPerson));
    yield put(notifyAboutChanges({ entityType: EntityType.DEAL, updated: [deal] }));
  } catch (error) {
    yield put(addDealParentPerson.failure());
    yield put(handleError({ error }));
  }
}

function* onRemoveDealParentPerson({ payload }: ReturnType<typeof removeDealParentPerson.request>) {
  try {
    const org: Organization = yield select(getOrganization);

    const deal: Deal = yield callApi("updateDeal", org.id, undefined, {
      id: payload,
      contact: null,
    });

    yield put(removeDealParentPerson.success(payload));
    yield put(notifyAboutChanges({ entityType: EntityType.DEAL, updated: [deal] }));
  } catch (error) {
    yield put(removeDealParentPerson.failure(payload));
    yield put(handleError({ error }));
  }
}

export function* onUpdateDeal({
  payload: {
    callback,
    customFields,
    deal,
    fieldsValueValidateCallback,
    groupIdsToAdd,
    groupIdsToRemove,
    layoutId,
    ownerChanged,
  },
}: ReturnType<typeof updateDeal.request>) {
  const intl = i18nService.getIntl();
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const recordData: DealRecordData = yield select(getRecordData);

    const readOnlyFields = new Set(
      dealFieldModel.fields
        .filter((field) => !field.isEditable || field.hasFeature(FieldFeature.CALCULATED_FIELD))
        .map((field) => field.name)
    );

    const nullableFields = dealFieldModel.fields.filter((field) =>
      field.hasFeature(FieldFeature.NULLABLE)
    );

    const changedCustomFields = Object.values(customFields ?? {}).filter((customField) => {
      if (!customField) {
        return false;
      }
      if (readOnlyFields.has(customField.esKey)) {
        return false;
      }

      const oldCustomField = recordData.deal?.customFields?.find(
        ({ customField: { id } }) => id === customField.customField.id
      );
      return !oldCustomField || !isEqual(oldCustomField.value, customField.value);
    });

    if (fieldsValueValidateCallback) {
      invariant(!!layoutId, "Must use layoutId to validate fields");

      // Validate fields values
      let fieldErrors: FieldValidationErrors[] = [];

      const standardFieldsValidationErrors: FieldValidationErrors[] = yield callApi(
        "validateStandardFields",
        orgId,
        layoutId,
        EntityType.DEAL,
        deal
      );
      if (standardFieldsValidationErrors.length) {
        fieldErrors = standardFieldsValidationErrors;
      }

      if (changedCustomFields.length) {
        const customFieldValidateResponse: FieldValidationErrors[] = yield callApi(
          "validateCustomFieldsValues",
          orgId,
          layoutId,
          EntityType.DEAL,
          undefined,
          changedCustomFields
        );

        if (customFieldValidateResponse.length) {
          fieldErrors = [...fieldErrors, ...customFieldValidateResponse];
        }
      }

      if (fieldErrors.length) {
        yield call(fieldsValueValidateCallback, fieldErrors);
        yield put(updateDeal.failure());
        return;
      }
    }

    let dealPayload = deal;
    if (readOnlyFields.size) {
      // implement proper fields deletion later
      dealPayload = omit(deal, Array.from(readOnlyFields)) as Identified;
    }
    if (nullableFields.length) {
      nullableFields.forEach((field) => {
        if (!field.hasNonEmptyValueFor(dealPayload)) {
          set(dealPayload, field.platformName, null);
        }
      });
    }

    let updatedDeal: Deal = yield callApi(
      "updateDeal",
      orgId,
      layoutId,
      dealPayload,
      undefined,
      false
    );

    if (changedCustomFields.length) {
      if (layoutId) {
        yield callApi(
          "upsertCustomFieldsValues",
          orgId,
          layoutId,
          EntityType.DEAL,
          deal.id,
          changedCustomFields
        );
      } else {
        loggingService.error(
          `Tried to update deal's (#${deal.id}) custom fields without providing layoutId`
        );
      }
    }

    const dealIds: Deal["id"][] = [deal.id];
    yield all([
      ...(groupIdsToAdd ?? []).map((groupId) =>
        callApi("addToGroup", orgId, groupId, EntityType.DEAL, dealIds)
      ),
      ...(groupIdsToRemove ?? []).map((groupId) =>
        callApi("deleteFromGroup", orgId, groupId, EntityType.DEAL, dealIds)
      ),
    ]);

    if (intl) {
      notification.success({
        message: getSuccessNotificationNode(intl, intl.formatMessage(messages.updateSuccess)),
      });
    }

    // now let's fetch it again since we updated a few more other details after
    // updateDeal api call
    try {
      updatedDeal = yield callApi("fetchDeal", orgId, deal.id, {
        includeAccessStatus: true,
        includeCustomFields: true,
        includeGroups: true,
        includeTerritories: true,
      });
    } catch (error) {
      // if owner changed, this is likely due to deal became inaccessible,
      // so we're just ignoring the error. Otherwise, re-throw it.
      if (!ownerChanged) {
        throw error;
      }
    }

    yield put(updateDeal.success(updatedDeal));
    yield put(notifyAboutChanges({ entityType: EntityType.DEAL, updated: [updatedDeal] }));
    if (callback) {
      yield call(callback, updatedDeal);
    }
  } catch (error) {
    yield put(updateDeal.failure());
    yield put(handleError({ defaultMessage: messages.updateError, error }));
  }
}

export function* onDeleteDeal() {
  try {
    const org: Organization = yield select(getOrganization);
    const deal: Deal = yield select(getDeal);
    yield callApi("deleteDeal", org.id, deal.id);
    yield put(deleteDeal.success());
    notification.success({
      description: i18nService.formatMessage(
        messages.deleteSuccessDescription,
        "To restore this record, go to Data in Settings."
      ),
      message: i18nService.formatMessage(messages.deleteSuccess, "Deal successfully deleted "),
    });
    yield put(notifyAboutChanges({ deletedIds: [deal.id], entityType: EntityType.DEAL }));
  } catch (error) {
    yield put(deleteDeal.failure());
    yield put(handleError({ error }));
  }
}

export function* onCreateDealNote({ payload }: ReturnType<typeof createDealNote.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const deal: Deal = yield select(getDeal);
    const note: Note = yield callApi("createNote", org.id, EntityType.DEAL, deal.id, payload);
    yield put(createDealNote.success());
    yield put(
      fetchNotes.request({
        filter: createEmptyFilterModel(),
        sort: [
          { field: noteFieldModel.getByName(NoteFieldName.UPDATED_AT)!, order: SortOrder.DESC },
        ],
      })
    );
    yield put(
      notifyAboutChanges({
        entityType: EntityType.DEAL,
        updated: [{ ...deal, notes: [...(deal.notes ?? []), note] }],
      })
    );
  } catch (error) {
    yield put(createDealNote.failure());
    yield put(handleError({ error }));
  }
}

export function* onUpdateDealNote({ payload }: ReturnType<typeof updateDealNote.request>) {
  try {
    const deal: Deal = yield select(getDeal);
    const updatedNote: Note = yield callApi("updateNote", EntityType.DEAL, deal.id, payload);
    yield put(updateDealNote.success(updatedNote));
    yield put(
      notifyAboutChanges({
        entityType: EntityType.DEAL,
        updated: [
          {
            ...deal,
            notes: (deal.notes ?? []).map((note) =>
              note.id === updatedNote.id ? updatedNote : note
            ),
          },
        ],
      })
    );
  } catch (error) {
    yield put(updateDealNote.failure(payload.id));
    yield put(handleError({ error }));
  }
}

export function* onUploadDealFiles({ payload }: ReturnType<typeof uploadDealFiles.request>) {
  try {
    const org: Organization = yield select(getOrganization);
    const deal: Deal = yield select(getDeal);
    const fileGroupId = payload.fileGroupId ?? uuid.v4();
    const responses: SettleResult<RawFile>[] = yield allSettled(
      payload.files.map((file) =>
        callApi("createFile", org.id, file, false, EntityType.DEAL, deal.id, {
          headers: {
            "x-mmc-file-group-id": fileGroupId,
          },
        })
      )
    );
    const fileList: FileListItem[] = responses.map((response, index) => ({
      file: payload.files[index],
      uploading: false,
      ...(response.error
        ? { errored: true, errorMessage: String(response.result) }
        : { errored: false, uploadedFile: response.result }),
    }));
    payload.callback?.(fileList);
    yield put(uploadDealFiles.success(fileList));
  } catch (error) {
    payload.callback?.(payload.files.map((file) => ({ errored: true, file, uploading: false })));
    yield put(uploadDealFiles.failure());
    yield put(handleError({ error }));
  }
}

export function* onDeleteDealFile({ payload }: ReturnType<typeof deleteDealFile.request>) {
  const deal: Deal = yield select(getDeal);
  try {
    const org: Organization = yield select(getOrganization);

    const notificationKey = `file_removal_${payload.id}`;
    const cancelled = new Promise<true>((resolve) => {
      notification.info({
        duration: NOTIFICATION_DURATION_WITH_ACTION,
        key: notificationKey,
        message: getFileRemovedNotificationNode(i18nService.getIntl(), payload, () => {
          resolve(true);
          notification.close(notificationKey);
        }),
      });
    });

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let markWindowClosed = (flag: true) => {};
    const windowClosed = new Promise<boolean>((resolve) => {
      markWindowClosed = resolve; // I need to "extract" it from here, so that I can call removeEventListener later
    });
    const documentListener = () => {
      if (document.visibilityState === "hidden") {
        markWindowClosed(true);
        notification.close(notificationKey);
      }
    };
    document.addEventListener("visibilitychange", documentListener);
    const windowListener = () => markWindowClosed(true);
    window.addEventListener("beforeunload", windowListener);

    const result: { cancelled?: Promise<true>; delete?: true; windowClosed?: Promise<true> } =
      yield race({
        cancelled,
        delete: delay(NOTIFICATION_DURATION_WITH_ACTION * 1000 /* since it is in seconds */),
        windowClosed,
      });

    window.removeEventListener("beforeunload", windowListener);
    document.removeEventListener("visibilitychange", documentListener);

    if (result.delete || result.windowClosed) {
      yield callApi("deleteEntityFile", org.id, EntityType.DEAL, deal.id, payload.id);
      yield put(deleteDealFile.success({ dealId: deal.id, file: payload, removed: true }));
    } else {
      yield put(deleteDealFile.success({ dealId: deal.id, file: payload, removed: false }));
    }
  } catch (error) {
    yield put(deleteDealFile.failure({ dealId: deal?.id, file: payload }));
    yield put(handleError({ error }));
  }
}

export function* onDownloadDealFile({ payload: file }: ReturnType<typeof downloadDealFile>) {
  try {
    const org: Organization = yield select(getOrganization);
    const deal: Deal = yield select(getDeal);

    const key = `download_file_${file.id}`;
    const timeout = setTimeout(() => {
      notification.info({
        duration: 0, // don't close automatically, we'll close it ourselves
        key,
        message: getYourDownloadWillStartShortlyNode(i18nService.getIntl(), file.name),
      });
    }, 2000);

    const fileBlob: Blob = yield callApi(
      "fetchFile",
      org.id,
      file.id,
      true,
      false,
      EntityType.DEAL,
      deal.id,
      { responseType: "blob", timeout: 0 }
    );
    clearTimeout(timeout);
    notification.close(key);

    yield call(downloadFileByUrl, window.URL.createObjectURL(fileBlob), file.name);
  } catch (error) {
    yield put(handleError({ error }));
  }
}

export function* onDeleteDealNote({ payload }: ReturnType<typeof deleteDealNote.request>) {
  try {
    const deal: Deal = yield select(getDeal);
    yield callApi("deleteNote", EntityType.DEAL, deal.id, payload);
    yield put(deleteDealNote.success(payload.id));
    yield put(fetchNotes.request());
    yield put(
      notifyAboutChanges({
        entityType: EntityType.DEAL,
        updated: [
          {
            ...deal,
            notes: (deal.notes ?? []).filter((note) => note.id !== payload.id),
          },
        ],
      })
    );
  } catch (error) {
    yield put(deleteDealNote.failure(payload.id));
    yield put(handleError({ error }));
  }
}

export function* onPostponeActivity({ payload }: ReturnType<typeof postponeActivity.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const layoutId = activityLayoutModel.getLayoutFor(payload).id;
    const { files, ...activity } = payload; // excluding files from payload

    const updatedActivity: Activity = yield callApi(
      "updateActivity",
      organization.id,
      layoutId,
      activity
    );
    yield put(postponeActivity.success(updatedActivity));

    notification.success({
      message: getSuccessNotificationNode(
        i18nService.getIntl(),
        i18nService.formatMessage(messages.postponeSuccess, "Activity postponed successfully")
      ),
    });
  } catch (error) {
    yield put(postponeActivity.failure(payload.id));
    yield put(handleError({ error }));
  }
}

export function* onToggleCompleteActivity({
  payload,
}: ReturnType<typeof toggleCompleteActivity.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const layoutId = activityLayoutModel.getLayoutFor(payload).id;
    const { files, ...activity } = payload; // excluding files from payload

    const response: Activity = yield callApi("updateActivity", organization.id, layoutId, {
      ...activity,
      completed: !activity.completed,
    });
    yield put(toggleCompleteActivity.success(response));

    notification.success({
      message: getSuccessNotificationNode(
        i18nService.getIntl(),
        i18nService.formatMessage(
          messages.toggleCompleteSuccess,
          `Activity marked as "${response.completed ? "Done" : "Not yet done"}"`,
          { completed: response.completed }
        )
      ),
    });
  } catch (error) {
    yield put(toggleCompleteActivity.failure(payload.id));
    yield put(handleError({ error }));
  }
}

export function* onUpdateActivityNote({ payload }: ReturnType<typeof updateActivityNote.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const layoutId = activityLayoutModel.getLayoutFor(payload.activity).id;
    const { files, ...activity } = payload.activity; // excluding files from payload

    const response: Activity = yield callApi("updateActivity", organization.id, layoutId, activity);
    yield put(
      updateActivityNote.success({
        id: response.id,
        note: response.note,
      })
    );
    payload.onSuccess();
  } catch (error) {
    yield put(updateActivityNote.failure(payload.activity.id));
    yield put(handleError({ error }));
  }
}

export function* onChangeActivitiesRecapRange({
  payload: recapRange,
}: ReturnType<typeof changeActivitiesRecapRange>) {
  yield call(localSettings.setRecapChartRange, recapRange);
}

export function* onChangeActivitiesSelectedActivityTypes({
  payload: activityTypes,
}: ReturnType<typeof changeActivitiesSelectedActivityTypes>) {
  yield put(updateMetadata.request({ recapChartTypesIds: activityTypes.map(({ id }) => id) }));
}

export function* onGoToCalendarSettings() {
  yield put(push(`${Path.SETTINGS}/${SettingPath.PERSONAL_CALENDAR_SYNC}`));
}

export function* onChangeAssociatedEntity({
  payload,
}: ReturnType<typeof changeAssociatedEntities.request>) {
  try {
    const associationsState: NextAssociationsState = yield call(getNextAssociationsState, {
      ...payload,
      entityType: EntityType.DEAL,
    });

    yield put(changeAssociatedEntities.success(associationsState));
  } catch (error) {
    yield put(changeAssociatedEntities.failure());
    yield put(handleError({ error }));
  }
}

export function* onUpdateDealFrequency({
  payload: { dealId },
}: ReturnType<typeof updateDealFrequency.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    const deal: Deal = yield callApi("fetchDeal", orgId, dealId, {
      includeAccessStatus: true,
      includeCustomFields: true,
      includeGroups: true,
      includeTerritories: true,
      includeUsersWithAccess: true,
    });
    yield put(updateDealFrequency.success(deal));
  } catch (error) {
    yield put(updateDealFrequency.failure());
    yield put(handleError({ error }));
  }
}

export function* onRelateUnrelateEntities({
  payload: {
    associatedCompany,
    associatedPerson,
    failureCallback,
    isPersonCorrectlyRelatedToCompany,
    successCallback,
  },
}: ReturnType<typeof relateUnrelateEntities.request>) {
  try {
    const orgId: Organization["id"] = yield select(getOrganizationId);
    let person: Person | undefined;
    if (!isPersonCorrectlyRelatedToCompany && associatedPerson && associatedCompany) {
      person = yield callApi(
        "updatePerson",
        orgId,
        undefined,
        {
          id: associatedPerson.id,
          accounts: [...associatedPerson.accounts, { id: associatedCompany.id }],
        },
        {
          includeUsersWithAccess: true,
        }
      );
    } else if (isPersonCorrectlyRelatedToCompany && associatedPerson && associatedCompany) {
      person = yield callApi(
        "updatePerson",
        orgId,
        undefined,
        {
          id: associatedPerson.id,
          accounts: associatedPerson.accounts.filter(({ id }) => id !== associatedCompany.id),
        },
        {
          includeUsersWithAccess: true,
        }
      );
    }
    successCallback?.();
    yield put(
      relateUnrelateEntities.success({ person, unrelate: isPersonCorrectlyRelatedToCompany })
    );
  } catch (error) {
    failureCallback?.();
    yield put(relateUnrelateEntities.failure());
    yield put(handleError({ error }));
  }
}

export function* onBulkUpdateFiles({
  payload: { callback, files },
}: ReturnType<typeof bulkUpdateFiles.request>) {
  try {
    const organization: Organization = yield select(getOrganization);
    const recordData: DealRecordData = yield select(getRecordData);

    if (!recordData.deal) {
      return;
    }

    yield callApi("bulkUpdateFile", organization.id, files);

    const updatedFilesResponse: ListResponse<RawFile> = yield callApi(
      "fetchEntityFiles",
      organization.id,
      EntityType.DEAL,
      recordData.deal.id,
      {
        $limit: 1000,
        $order: "-createdAt",
      }
    );

    if (callback) {
      yield call(callback);
    }

    yield put(bulkUpdateFiles.success(updatedFilesResponse.data));
  } catch (error) {
    yield put(bulkUpdateFiles.failure(error));
    yield put(handleError({ error }));
  }
}

export function* onFetchNotes() {
  try {
    const filter: FilterModel | undefined = yield select(getNotesFilter);
    const sort: SortModel = yield select(getNotesSort);
    const recordData: DealRecordData = yield select(getRecordData);

    if (!recordData.deal) {
      return;
    }

    const response: ListResponse<Note> = yield callApi(
      "fetchNotes",
      EntityType.DEAL,
      recordData.deal.id,
      {
        $filters: filter ? convertToPlatformFilterModel(filter, [], noteFieldModel) : undefined,
        $limit: 1000,
        $order: convertToPlatformSortModel(sort),
      }
    );
    yield put(fetchNotes.success({ data: response.data, total: response.accessible }));
  } catch (error) {
    yield put(fetchNotes.failure());
    yield put(handleError({ error }));
  }
}

export function* onFetchRelatedEnties() {
  try {
    const recordData: DealRecordData = yield select(getRecordData);
    if (!recordData.deal) {
      return;
    }
    const organization: Organization = yield select(getOrganization);

    const deal: Deal | undefined = yield callApi("fetchDeal", organization.id, recordData.deal.id);

    if (!deal) {
      yield put(fetchRelatedEntities.failure());
      return;
    }

    const companyPinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [
      EntityType.COMPANY,
    ]);
    const personPinLegends: PinLegendsFilter = yield call(getPinLegendsFilter, [EntityType.PERSON]);

    const [companyParent, personParent]: [
      SettleResult<Company | undefined>,
      SettleResult<Person | undefined>,
    ] = yield allSettled([
      deal.account
        ? callApi("fetchCompany", organization.id, deal.account.id, {
            includeAccessStatus: true,
            includeCustomFields: true,
            includeGroups: true,
            includeTerritories: true,
            pinLegends: companyPinLegends,
          })
        : undefined,
      deal.contact
        ? callApi("fetchPerson", organization.id, deal.contact.id, {
            includeAccessStatus: true,
            includeCustomFields: true,
            includeGroups: true,
            includeTerritories: true,
            pinLegends: personPinLegends,
          })
        : undefined,
    ]);

    const associations: NextAssociationsState = yield call(getNextAssociationsState, {
      company: !companyParent.error ? companyParent.result : deal.account,
      entityType: EntityType.DEAL,
      person: !personParent.error ? personParent.result : deal.contact,
    });

    const associatedCompany = processAssociatedEntity(deal.account, companyParent);
    const associatedPerson = processAssociatedEntity(deal.contact, personParent);

    yield put(
      fetchRelatedEntities.success({
        ...associations,
        associatedCompany,
        associatedPerson,
      })
    );
  } catch (error) {
    yield put(fetchRelatedEntities.failure());
    yield put(handleError({ error }));
  }
}

export function* dealRecordSaga() {
  yield takeEvery(addDealParentCompany.request, onAddDealParentCompany);
  yield takeEvery(addDealParentPerson.request, onAddDealParentPerson);
  yield takeEvery(createDealNote.request, onCreateDealNote);
  yield takeLatest(changeAssociatedEntities.request, onChangeAssociatedEntity);
  yield takeEvery(deleteDeal.request, onDeleteDeal);
  yield takeEvery(deleteDealFile.request, onDeleteDealFile);
  yield takeEvery(deleteDealNote.request, onDeleteDealNote);
  yield takeEvery(downloadDealFile, onDownloadDealFile);
  yield takeLatest(fetchDealActivities.request, onFetchDealActivities);
  yield takeLatest(
    fetchDealActivitiesCompletedByType.request,
    onFetchDealActivitiesCompletedByType
  );
  yield takeLatest(fetchPreviewData.request, onFetchPreviewData);
  yield takeLatest(fetchDealActivitiesTotal.request, onFetchDealActivitiesTotal);
  yield takeLatest(fetchDeal.request, onFetchDeal);
  yield takeEvery(postponeActivity.request, onPostponeActivity);
  yield takeEvery(removeDealParentCompany.request, onRemoveDealParentCompany);
  yield takeEvery(removeDealParentPerson.request, onRemoveDealParentPerson);
  yield takeEvery(toggleCompleteActivity.request, onToggleCompleteActivity);
  yield takeEvery(updateActivityNote.request, onUpdateActivityNote);
  yield takeEvery(updateDeal.request, onUpdateDeal);
  yield takeEvery(uploadDealFiles.request, onUploadDealFiles);
  yield takeEvery(updateDealNote.request, onUpdateDealNote);
  yield takeEvery(changeActivitiesRecapRange, onChangeActivitiesRecapRange);
  yield takeEvery(changeActivitiesSelectedActivityTypes, onChangeActivitiesSelectedActivityTypes);
  yield takeEvery(goToCalendarSettings, onGoToCalendarSettings);
  yield takeEvery(fetchThumbnail, onFetchThumbnail);
  yield takeEvery(updateDealFrequency.request, onUpdateDealFrequency);
  yield takeEvery(relateUnrelateEntities.request, onRelateUnrelateEntities);
  yield takeLatest(bulkUpdateFiles.request, onBulkUpdateFiles);
  yield takeLatest(fetchNotes.request, onFetchNotes);
  yield takeLatest(fetchRelatedEntities.request, onFetchRelatedEnties);
}
